import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  inject,
  OnInit,
} from '@angular/core'

import * as monaco from 'monaco-editor/esm/vs/editor/editor.api'
import { ThemeService } from '../../themes/service/theme.service'

declare global {
  interface Window {
    MonacoEnvironment?: monaco.Environment
  }
}

// Worker config
if (typeof window !== 'undefined' && !(window as any).MonacoEnvironment) {
  ;(window as any).MonacoEnvironment = {
    getWorkerUrl: function (_moduleId: string, label: string) {
      if (label === 'json')
        return '/assets/monaco/vs/language/json/json.worker.js'
      if (label === 'css' || label === 'scss' || label === 'less')
        return '/assets/monaco/vs/language/css/css.worker.js'
      if (label === 'html' || label === 'handlebars' || label === 'razor')
        return '/assets/monaco/vs/language/html/html.worker.js'
      if (label === 'typescript' || label === 'javascript')
        return '/assets/monaco/vs/language/typescript/ts.worker.js'
      return '/assets/monaco/vs/editor/editor.worker.js'
    },
  }
}

@Component({
  selector: 'hyperkit-code-editor',
  standalone: true,
  imports: [],
  template: `
    <div class="editor-container" [style.--editor-height]="height">
      <div #editorContainer class="monaco-container"></div>
    </div>
  `,
  styles: [
    `
      .editor-container {
        width: 100%;
        height: var(--editor-height, 300px);
        border: 1px solid var(--border-muted);
        border-radius: 0px;
        background: var(--background-elevation-ground);
        background-color: var(--background-elevation-ground);
        overflow: hidden;
        color: var(--foreground-emphasized);
      }
      .monaco-container {
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class MonacoComponent
  implements AfterViewInit, OnDestroy, OnChanges, OnInit
{
  private readonly themeService = inject(ThemeService)

  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef
  @Input() value = ''
  @Input() options: monaco.editor.IStandaloneEditorConstructionOptions = {}
  @Output() valueChange = new EventEmitter<string>()
  @Input() height = '300px'

  private editor: monaco.editor.IStandaloneCodeEditor | null = null
  private resizeObserver: ResizeObserver | null = null

  private theme = this.themeService.getTheme()

  ngOnInit(): void {
    this.loadMonacoStyles()
  }

  private loadMonacoStyles(): void {
    if (typeof document === 'undefined') {
      return
    }

    const styles = ['/assets/monaco/vs/editor/editor.main.css']

    styles.forEach((href) => {
      if (!document.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = href
        document.head.appendChild(link)
      }
    })
  }

  ngAfterViewInit(): void {
    const defaultOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
      value: this.value,
      language: 'json',
      theme: this.theme === 'dark' ? 'vs-dark' : 'vs',
      fontSize: 14,
      automaticLayout: true,
      minimap: { enabled: false },
      wordWrap: 'off',
      lineNumbers: 'on',
      renderWhitespace: 'all',
      scrollBeyondLastLine: false,
    }

    setTimeout(() => {
      this.editor = monaco.editor.create(this.editorContainer.nativeElement, {
        ...defaultOptions,
        ...this.options,
        value: this.value,
      })

      this.editor.onDidChangeModelContent(() => {
        const newValue = this.editor?.getValue() ?? ''
        this.valueChange.emit(newValue)
      })

      this.resizeObserver = new ResizeObserver(() => {
        this.editor?.layout()
      })

      this.resizeObserver.observe(this.editorContainer.nativeElement)
    }, 100)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && this.editor && !changes['value'].firstChange) {
      requestAnimationFrame(() => {
        const currentValue = this.editor?.getValue()
        if (this.value !== currentValue) {
          this.editor?.setValue(this.value)
        }
      })
    }
  }

  ngOnDestroy(): void {
    this.editor?.dispose()
    this.resizeObserver?.disconnect()
  }
}
